import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";

import { UserType } from "@core/enums/user-type";
import { DialogHelper } from "@core/helpers/dialog-helper";
import { AccountHistoricValue } from "@core/models/account-historic-values.model";
import { AccountRiskProfile } from "@core/models/account-risk-profile.model";
import { CompositionRiskProfile } from "@core/models/composition-risk-profile.model";
import { CompositionVersion } from "@core/models/composition-version.model";
import { Configuration } from "@core/models/configuration.model";
import {
  DisbursementWithTax,
  TDisbursementsWithTaxPaginated,
} from "@core/models/disbursement-with-tax.model";
import { AccountFund } from "@core/models/fund.model";
import { FundRatioHistory } from "@core/models/fund-ratio-history.model";
import { FundWithRelated } from "@core/models/fund-with-related.model";
import { GeneralAccountCorrection } from "@core/models/general-account-correction.model";
import {
  GroupedGfhTransaction,
  TGroupedGfhTransactionsPaginated,
} from "@core/models/grouped-gfh-transaction.model";
import { RiskProfile } from "@core/models/risk-profile.model";
import { TAllClients, TAllClientsPaginated } from "@core/models/user.model";
import { ClientsAndAccountsService } from "@core/services/clients-and-accounts.service";
import { NotificationService } from "@core/services/notification.service";
import { TransactionService } from "@core/services/transaction.service";
import { TPagination } from "@custom-types/sorting";
import { ToasterSeverities } from "@enums/toaster-severities";
import * as clientsAndAccountsActions from "@features/clients-and-accounts/store/clients-and-accounts.actions";
import { SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS } from "@features/clients-and-accounts/store/clients-and-accounts.actions";
import { LOAD_LATEST_TRADING } from "@features/trading/store/trading.actions";
import { ExtendedGfhAccount } from "@models/extended-gfh-account.model";
import { GfhAccountClosure } from "@models/gfh-account-closure.model";
import { InternalTransfer } from "@models/internal-transfer.model";
import { Quotation } from "@models/quotation.model";
import { WithdrawalAssignment } from "@models/withdrawal-assignment.model";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action, Store } from "@ngrx/store";
import {
  catchError,
  concatMap,
  exhaustMap,
  mergeMap,
  Observable,
  of,
  switchMap,
  withLatestFrom,
} from "rxjs";

import {
  SELECT_ACTIVE_ACCOUNT,
  SELECT_CLIENT_ACCOUNTS_BY_CLIENT_ID,
  SELECT_CLIENT_ACCOUNTS_PAGINATION,
} from "./clients-and-accounts.selectors";

@Injectable()
export class ClientsAndAccountsEffects {
  loadAllClients$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_ALL_CLIENTS),
      mergeMap((action: { searchTerm?: string }): Observable<Action> => {
        return this.clientsService.getClients$(action.searchTerm).pipe(
          switchMap(
            (
              response: Array<TAllClients> | TAllClientsPaginated
            ): Observable<Action> => {
              const clientsList = Array.isArray(response)
                ? response
                : response.data;
              return of(
                clientsAndAccountsActions.SET_ALL_CLIENTS({
                  clientsList,
                })
              );
            }
          ),
          catchError((): Observable<Action> => {
            return of(
              clientsAndAccountsActions.SET_ALL_CLIENTS({
                clientsList: [],
              })
            );
          })
        );
      })
    );
  });
  loadAllClientsPaginated$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_ALL_CLIENTS_PAGINATED),
      withLatestFrom(this.store.select(SELECT_CLIENT_ACCOUNTS_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          {
            searchTerm?: string;
            pagination?: Partial<TPagination>;
          },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(
            clientsAndAccountsActions.SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS({
              loading: true,
            })
          );
          return this.clientsService
            .getClients$(action.searchTerm, action.pagination ?? pagination)
            .pipe(
              switchMap(
                (
                  response: Array<TAllClients> | TAllClientsPaginated
                ): Observable<Action> => {
                  this.store.dispatch(
                    clientsAndAccountsActions.SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS(
                      { loading: false }
                    )
                  );
                  if ("data" in response && "length" in response) {
                    const { data, ...pagination } = response;
                    return of(
                      clientsAndAccountsActions.SET_ALL_CLIENTS_PAGINATED({
                        clientsList: data,
                      }),
                      clientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_PAGINATION(
                        {
                          pagination: pagination,
                        }
                      ),
                      clientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_LIST_LENGTH(
                        {
                          length: response.length,
                        }
                      )
                    );
                  }
                  return of(
                    clientsAndAccountsActions.SET_ALL_CLIENTS_PAGINATED({
                      clientsList: response,
                    })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  clientsAndAccountsActions.SET_ALL_CLIENTS_PAGINATED({
                    clientsList: [],
                  }),
                  clientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_PAGINATION({
                    pagination: undefined,
                  }),
                  clientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_LIST_LENGTH({
                    length: 0,
                  }),
                  clientsAndAccountsActions.SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS(
                    { loading: false }
                  )
                );
              })
            );
        }
      )
    );
  });

  loadPrivateClients$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_PRIVATE_CLIENTS),
      withLatestFrom(this.store.select(SELECT_CLIENT_ACCOUNTS_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          {
            searchTerm?: string;
            pagination?: Partial<TPagination>;
          },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(
            clientsAndAccountsActions.SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS({
              loading: true,
            })
          );
          return this.clientsService
            .getClients$(
              action.searchTerm,
              action.pagination ?? pagination,
              UserType.private
            )
            .pipe(
              switchMap(
                (
                  response: Array<TAllClients> | TAllClientsPaginated
                ): Observable<Action> => {
                  this.store.dispatch(
                    clientsAndAccountsActions.SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS(
                      { loading: false }
                    )
                  );
                  if ("data" in response && "length" in response) {
                    const { data, ...pagination } = response;
                    return of(
                      clientsAndAccountsActions.SET_PRIVATE_CLIENTS({
                        clientsList: data,
                      }),
                      clientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_LIST_LENGTH(
                        { length: pagination.length }
                      ),
                      clientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_PAGINATION(
                        { pagination }
                      )
                    );
                  }
                  return of(
                    clientsAndAccountsActions.SET_PRIVATE_CLIENTS({
                      clientsList: response,
                    })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  clientsAndAccountsActions.SET_PRIVATE_CLIENTS({
                    clientsList: [],
                  }),
                  clientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_LIST_LENGTH({
                    length: 0,
                  }),
                  clientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_PAGINATION({
                    pagination: undefined,
                  }),
                  clientsAndAccountsActions.SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS(
                    { loading: false }
                  )
                );
              })
            );
        }
      )
    );
  });

  loadCompanyClients$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_COMPANY_CLIENTS),
      withLatestFrom(this.store.select(SELECT_CLIENT_ACCOUNTS_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          {
            searchTerm?: string;
            pagination?: Partial<TPagination>;
          },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(
            clientsAndAccountsActions.SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS({
              loading: true,
            })
          );
          return this.clientsService
            .getClients$(
              action.searchTerm,
              action.pagination ?? pagination,
              UserType.company
            )
            .pipe(
              switchMap(
                (
                  response: Array<TAllClients> | TAllClientsPaginated
                ): Observable<Action> => {
                  this.store.dispatch(
                    clientsAndAccountsActions.SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS(
                      { loading: false }
                    )
                  );
                  if ("data" in response && "length" in response) {
                    const { data, ...pagination } = response;
                    return of(
                      clientsAndAccountsActions.SET_COMPANY_CLIENTS({
                        clientsList: data,
                      }),
                      clientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_LIST_LENGTH(
                        { length: pagination.length }
                      ),
                      clientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_PAGINATION(
                        { pagination }
                      )
                    );
                  }
                  return of(
                    clientsAndAccountsActions.SET_COMPANY_CLIENTS({
                      clientsList: response,
                    })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  clientsAndAccountsActions.SET_COMPANY_CLIENTS({
                    clientsList: [],
                  }),
                  clientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_LIST_LENGTH({
                    length: 0,
                  }),
                  clientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_PAGINATION({
                    pagination: undefined,
                  }),
                  clientsAndAccountsActions.SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS(
                    { loading: false }
                  )
                );
              })
            );
        }
      )
    );
  });

  loadClientAccountList$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_CLIENT_ACCOUNTS),
      // switchMap, not mergeMap: the account list is a single shared store slice, so a
      // late response for a previously expanded client would otherwise overwrite the
      // accounts of the client that is currently expanded.
      switchMap((action: { id: string }): Observable<Action> => {
        this.store.dispatch(
          clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS({
            loading: true,
          })
        );
        return this.clientsService.getClientAccountList$(action.id).pipe(
          switchMap(
            (accountList: Array<ExtendedGfhAccount>): Observable<Action> => {
              this.store.dispatch(
                clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS(
                  { loading: false }
                )
              );
              return of(
                clientsAndAccountsActions.SET_CLIENT_ACCOUNTS({
                  clientId: action.id,
                  accountList: accountList,
                })
              );
            }
          ),
          catchError((): Observable<Action> => {
            this.store.dispatch(
              clientsAndAccountsActions.SET_CLIENT_ACCOUNTS({
                clientId: action.id,
                accountList: [],
              })
            );
            return of(
              clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS(
                { loading: false }
              )
            );
          })
        );
      })
    );
  });

  saveClient$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.SAVE_CLIENT),
      mergeMap(
        (action: {
          client: Partial<TAllClients>;
          id: string | undefined;
          userType?: UserType;
        }): Observable<Action> => {
          this.store.dispatch(
            SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS({ loading: true })
          );
          return this.clientsService.saveClient$(action.client, action.id).pipe(
            switchMap((): Observable<Action> => {
              this.dialog.closeAll();
              this.store.dispatch(
                SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS({ loading: false })
              );
              // Same test the service uses to choose POST over PUT.
              this.notificationService.show(
                ToasterSeverities.Success,
                action.id === "" ? "CLIENT_CREATED" : "CLIENT_UPDATED"
              );
              // Reload the overview for creates as well as edits: a newly created
              // client is otherwise missing from the list until a manual refresh.
              switch (action.userType) {
                case UserType.private:
                  return of(clientsAndAccountsActions.LOAD_PRIVATE_CLIENTS({}));
                case UserType.company:
                  return of(clientsAndAccountsActions.LOAD_COMPANY_CLIENTS({}));
                default:
                  return of(
                    clientsAndAccountsActions.LOAD_ALL_CLIENTS_PAGINATED({})
                  );
              }
            }),
            catchError((response: HttpErrorResponse): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Error,
                action.id === ""
                  ? "CLIENT_CREATE_ERROR"
                  : "CLIENT_UPDATE_ERROR",
                response
              );
              return of(
                SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS({ loading: false })
              );
            })
          );
        }
      )
    );
  });

  checkClientBsn$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.CHECK_CLIENT_BSN),
      mergeMap(
        (action: {
          clientBsn: string;
          countryCode: string;
        }): Observable<Action> => {
          return this.clientsService
            .checkClientBsn$(action.clientBsn, action.countryCode)
            .pipe(
              switchMap((valid: boolean): Observable<Action> => {
                return of(
                  clientsAndAccountsActions.SET_CLIENT_BSN_VALIDITY({
                    valid: valid,
                  })
                );
              }),
              catchError((): Observable<Action> => {
                return of();
              })
            );
        }
      )
    );
  });

  resetPassword$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.RECOVER_PASSWORD),
      mergeMap((action: { clientId: string }): Observable<Action> => {
        return this.clientsService.recoverPassword$(action.clientId).pipe(
          switchMap((): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Success,
              "RESET_PASSWORD_SUCCESS"
            );
            return of();
          }),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  sendNotificationLetter$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.SEND_NOTIFICATION_LETTER),
      mergeMap(
        (action: {
          clientId: string;
          generalLetter: boolean;
          sendToCustomer: boolean;
        }): Observable<Action> => {
          return this.clientsService
            .sendLetter$(
              action.clientId,
              action.generalLetter,
              action.sendToCustomer
            )
            .pipe(
              switchMap((): Observable<Action> => {
                if (action.generalLetter) {
                  this.notificationService.show(
                    ToasterSeverities.Success,
                    "LETTER_CREATED_SUCCESSFULLY"
                  );
                } else {
                  this.notificationService.show(
                    ToasterSeverities.Success,
                    "MODIFICATION_LETTER_CREATED_SUCCESSFULLY"
                  );
                }
                return of();
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "LETTER_CREATION_ERROR",
                  response
                );
                return of();
              })
            );
        }
      )
    );
  });

  saveAccount$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.SAVE_ACCOUNT),
      mergeMap(
        (action: {
          account: Partial<ExtendedGfhAccount>;
          id: string | undefined;
          userType?: UserType;
        }): Observable<Action> => {
          this.store.dispatch(
            SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS({ loading: true })
          );
          return this.clientsService
            .saveAccount$(action.account, action.id)
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeAll();
                this.store.dispatch(
                  SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS({ loading: false })
                );
                // Same test the service uses to choose POST over PUT.
                this.notificationService.show(
                  ToasterSeverities.Success,
                  action.id === ""
                    ? "ACCOUNT_CREATED"
                    : "ACCOUNT_UPDATE_SUCCESS"
                );
                if (action.account?.clientId) {
                  return of(
                    clientsAndAccountsActions.LOAD_CLIENT_ACCOUNTS({
                      id: action.account.clientId,
                    })
                  );
                }
                return of();
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  action.id === ""
                    ? "ACCOUNT_CREATE_ERROR"
                    : "ACCOUNT_UPDATE_ERROR",
                  response
                );
                this.store.dispatch(
                  SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS({ loading: false })
                );
                return of();
              })
            );
        }
      )
    );
  });

  loadAccountDetails$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_ACCOUNT_DETAILS),
      mergeMap((action: { id: string }): Observable<Action> => {
        return this.clientsService.getAccountDetails$(action.id).pipe(
          switchMap((account: ExtendedGfhAccount): Observable<Action> => {
            return of(
              clientsAndAccountsActions.SET_ACCOUNT_DETAILS({
                account: account,
              })
            );
          }),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  loadAccount$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_ACCOUNT),
      withLatestFrom(this.store.select(SELECT_CLIENT_ACCOUNTS_BY_CLIENT_ID)),
      mergeMap(
        ([action, accountsByClientId]: [
          { accountNumber: string },
          Record<string, Array<ExtendedGfhAccount>>,
        ]): Observable<Action> => {
          this.store.dispatch(
            SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS({ loading: true })
          );
          this.store.dispatch(
            clientsAndAccountsActions.SET_FUND_LIST_LOADING_STATUS({
              loading: true,
            })
          );
          return this.clientsService.getAccount$(action.accountNumber).pipe(
            switchMap((account: ExtendedGfhAccount): Observable<Action> => {
              this.store.dispatch(
                SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS({ loading: false })
              );
              this.store.dispatch(
                clientsAndAccountsActions.SET_FUND_LIST_LOADING_STATUS({
                  loading: false,
                })
              );
              if (!account.errorAccount) {
                // Only the owner's own portfolio is prefetched here, keyed by owner:
                // a cached list for some other client must never stand in for it.
                if (!accountsByClientId[account.clientId]?.length) {
                  this.store.dispatch(
                    clientsAndAccountsActions.LOAD_CLIENT_ACCOUNTS({
                      id: account.clientId,
                    })
                  );
                }
                return of(
                  clientsAndAccountsActions.SET_ACCOUNT_DETAILS({
                    account: account,
                  }),
                  clientsAndAccountsActions.LOAD_ACCOUNT_CONFIGURATION({
                    id: account.id,
                  })
                );
              }
              return of(
                clientsAndAccountsActions.LOAD_ACCOUNT_DETAILS({
                  id: "errorAccount",
                })
              );
            }),
            catchError((): Observable<Action> => {
              this.store.dispatch(
                clientsAndAccountsActions.SET_FUND_LIST_LOADING_STATUS({
                  loading: false,
                })
              );
              return of(
                clientsAndAccountsActions.SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS(
                  { loading: false }
                )
              );
            })
          );
        }
      )
    );
  });

  loadAccountTransactions$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_ACCOUNT_TRANSACTIONS),
      withLatestFrom(this.store.select(SELECT_CLIENT_ACCOUNTS_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          {
            id: string;
            pagination?: Partial<TPagination>;
          },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(
            clientsAndAccountsActions.SET_ACCOUNT_TRANSACTIONS_LOADING_STATUS({
              loading: true,
            })
          );
          return this.clientsService
            .getTransactionsForAccount$(
              action.id,
              action.pagination ?? pagination
            )
            .pipe(
              switchMap(
                (
                  response:
                    | Array<GroupedGfhTransaction>
                    | TGroupedGfhTransactionsPaginated
                ): Observable<Action> => {
                  this.store.dispatch(
                    clientsAndAccountsActions.SET_ACCOUNT_TRANSACTIONS_LOADING_STATUS(
                      { loading: false }
                    )
                  );
                  if ("data" in response && "totalRecords" in response) {
                    const { data, ...pagination } = response;
                    return of(
                      clientsAndAccountsActions.SET_ACCOUNT_TRANSACTIONS({
                        transactions: data,
                      }),
                      clientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_PAGINATION(
                        { pagination }
                      ),
                      clientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_LIST_LENGTH(
                        { length: pagination.totalRecords }
                      )
                    );
                  }
                  return of(
                    clientsAndAccountsActions.SET_ACCOUNT_TRANSACTIONS({
                      transactions: response,
                    })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  clientsAndAccountsActions.SET_ACCOUNT_TRANSACTIONS({
                    transactions: [],
                  }),
                  clientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_PAGINATION({
                    pagination: undefined,
                  }),
                  clientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_LIST_LENGTH({
                    length: 0,
                  }),
                  clientsAndAccountsActions.SET_ACCOUNT_TRANSACTIONS_LOADING_STATUS(
                    { loading: false }
                  )
                );
              })
            );
        }
      )
    );
  });

  loadGeneralAccountCorrectionList$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_GENERAL_ACCOUNT_CORRECTION_LIST),
      withLatestFrom(this.store.select(SELECT_ACTIVE_ACCOUNT)),
      mergeMap(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ([_, activeAccount]: [
          unknown,
          ExtendedGfhAccount,
        ]): Observable<Action> => {
          this.store.dispatch(
            clientsAndAccountsActions.SET_CORRECTION_LOADING_STATUS({
              loading: true,
            })
          );
          return this.clientsService
            .generalAccountCorrectionList$(activeAccount.id)
            .pipe(
              switchMap(
                (
                  correctionList: Array<GeneralAccountCorrection>
                ): Observable<Action> => {
                  this.store.dispatch(
                    clientsAndAccountsActions.SET_CORRECTION_LOADING_STATUS({
                      loading: false,
                    })
                  );
                  return of(
                    clientsAndAccountsActions.SET_GENERAL_ACCOUNT_CORRECTION_LIST(
                      { correctionList }
                    )
                  );
                }
              ),
              catchError((): Observable<Action> => {
                this.store.dispatch(
                  clientsAndAccountsActions.SET_CORRECTION_LOADING_STATUS({
                    loading: false,
                  })
                );
                return of();
              })
            );
        }
      )
    );
  });

  createCorrection$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.CREATE_CORRECTION),
      withLatestFrom(this.store.select(SELECT_ACTIVE_ACCOUNT)),
      mergeMap(
        ([action, activeAccount]: [
          { correction: Partial<GeneralAccountCorrection> },
          ExtendedGfhAccount,
        ]): Observable<Action> => {
          return this.clientsService
            .createCorrection$({
              ...action.correction,
              gfhAccount: activeAccount,
              gfhAccountId: activeAccount.id,
            })
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeAll();
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "CORRECTION_CREATION_SUCCESS"
                );
                return of();
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "CORRECTION_CREATION_ERROR",
                  response
                );
                return of();
              })
            );
        }
      )
    );
  });

  exportTransactions$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.EXPORT_TRANSACTIONS),
      withLatestFrom(this.store.select(SELECT_ACTIVE_ACCOUNT)),
      mergeMap(
        ([action, gfhAccount]: [
          { startDate: string; endDate: string },
          ExtendedGfhAccount,
        ]): Observable<Action> => {
          return this.clientsService
            .exportTransactions$({ ...action, gfhAccountId: gfhAccount.id })
            .pipe(
              switchMap((): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "TRANSACTION_EXPORT_BACKGROUND"
                );
                return of();
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "ERROR_BACKGROUND",
                  response
                );
                return of();
              })
            );
        }
      )
    );
  });

  loadAccountHistoricValueList$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_ACCOUNT_HISTORIC_VALUE_LIST),
      mergeMap(
        (action: {
          accountId: string;
          start: number;
          take: number;
        }): Observable<Action> => {
          this.store.dispatch(
            clientsAndAccountsActions.SET_FUND_LIST_LOADING_STATUS({
              loading: true,
            })
          );
          return this.clientsService
            .getAccountHistoricValueList$(
              action.accountId,
              action.start,
              action.take
            )
            .pipe(
              switchMap(
                (
                  accountHistoricValueList: Array<AccountHistoricValue>
                ): Observable<Action> => {
                  this.store.dispatch(
                    clientsAndAccountsActions.SET_FUND_LIST_LOADING_STATUS({
                      loading: false,
                    })
                  );
                  return of(
                    clientsAndAccountsActions.SET_ACCOUNT_HISTORIC_VALUE_LIST({
                      accountHistoricValueList,
                    })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  clientsAndAccountsActions.SET_FUND_LIST_LOADING_STATUS({
                    loading: false,
                  })
                );
              })
            );
        }
      )
    );
  });

  loadFundList$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_FUND_LIST),
      mergeMap((): Observable<Action> => {
        this.store.dispatch(
          clientsAndAccountsActions.SET_FUND_LIST_LOADING_STATUS({
            loading: true,
          })
        );
        return this.transactionService.getFundList$().pipe(
          switchMap((fundList: Array<FundWithRelated>): Observable<Action> => {
            this.store.dispatch(
              clientsAndAccountsActions.SET_FUND_LIST_LOADING_STATUS({
                loading: false,
              })
            );
            return of(clientsAndAccountsActions.SET_FUND_LIST({ fundList }));
          }),
          catchError((): Observable<Action> => {
            return of(
              clientsAndAccountsActions.SET_FUND_LIST_LOADING_STATUS({
                loading: false,
              })
            );
          })
        );
      })
    );
  });

  loadAccountConfiguration$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_ACCOUNT_CONFIGURATION),
      mergeMap((action: { id?: string }): Observable<Action> => {
        this.store.dispatch(
          clientsAndAccountsActions.SET_FUND_LIST_LOADING_STATUS({
            loading: true,
          })
        );
        return this.clientsService.getAccountConfiguration$().pipe(
          switchMap((configuration: Configuration): Observable<Action> => {
            this.store.dispatch(
              clientsAndAccountsActions.SET_FUND_LIST_LOADING_STATUS({
                loading: false,
              })
            );
            if (action.id) {
              if (configuration.showHistoricFundValues) {
                this.store.dispatch(
                  clientsAndAccountsActions.LOAD_ACCOUNT_HISTORIC_VALUE_LIST({
                    accountId: action.id,
                    start: 0,
                    take: 5,
                  })
                );
              } else {
                this.store.dispatch(clientsAndAccountsActions.LOAD_FUND_LIST());
              }
              if (configuration.tradeExternally) {
                this.store.dispatch(LOAD_LATEST_TRADING({ definitive: false }));
              }
            }
            return of(
              clientsAndAccountsActions.SET_ACCOUNT_CONFIGURATION({
                configuration,
              })
            );
          }),
          catchError((): Observable<Action> => {
            this.store.dispatch(
              clientsAndAccountsActions.SET_FUND_LIST_LOADING_STATUS({
                loading: false,
              })
            );
            return of();
          })
        );
      })
    );
  });

  loadWithdrawalAssignmentList$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_WITHDRAWAL_ASSIGNMENT),
      mergeMap((action: { id: string }): Observable<Action> => {
        this.store.dispatch(
          clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS({
            loading: true,
          })
        );
        return this.clientsService.getWithdrawalAssignmentList$(action.id).pipe(
          switchMap((list: Array<WithdrawalAssignment>): Observable<Action> => {
            this.store.dispatch(
              clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS(
                { loading: false }
              )
            );
            return of(
              clientsAndAccountsActions.SET_WITHDRAWAL_ASSIGNMENT({
                withdrawalList: list,
              })
            );
          }),
          catchError((): Observable<Action> => {
            this.store.dispatch(
              clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS(
                { loading: false }
              )
            );
            return of(
              clientsAndAccountsActions.SET_WITHDRAWAL_ASSIGNMENT({
                withdrawalList: [],
              })
            );
          })
        );
      })
    );
  });

  deleteWithdrawalAssignment$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.DELETE_WITHDRAWAL_ASSIGNMENT),
      concatMap(
        (action: { id: string; accountId: string }): Observable<Action> => {
          this.store.dispatch(
            clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS({
              loading: true,
            })
          );
          return this.clientsService
            .deleteWithdrawalAssignment$(action.id)
            .pipe(
              switchMap((): Observable<Action> => {
                this.store.dispatch(
                  clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS(
                    { loading: false }
                  )
                );
                return of(
                  clientsAndAccountsActions.LOAD_WITHDRAWAL_ASSIGNMENT({
                    id: action.accountId,
                  })
                );
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "WITHDRAWAL_ASSIGNMENT_DELETE_ERROR",
                  response
                );
                this.store.dispatch(
                  clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS(
                    { loading: false }
                  )
                );
                return of();
              })
            );
        }
      )
    );
  });

  getTradeForAccount$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.GET_TRADE_FOR_ACCOUNT),
      mergeMap((action: { accountId: string }): Observable<Action> => {
        return this.clientsService.getTradeForAccount$(action.accountId).pipe(
          switchMap((): Observable<Action> => {
            this.dialog.closeAll();
            this.notificationService.show(
              ToasterSeverities.Success,
              "TRADING_BACKGROUND"
            );
            return of();
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "ERROR_BACKGROUND",
              response
            );
            return of();
          })
        );
      })
    );
  });

  saveWithdrawalAssignment$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.SAVE_WITHDRAWAL_ASSIGNMENT),
      // A second click while the first save is running is ignored; a double click used to save the
      // withdrawal twice (TL-1198).
      exhaustMap(
        (action: {
          withdrawalAssignment: Partial<WithdrawalAssignment>;
          accountId: string;
        }): Observable<Action> => {
          this.store.dispatch(
            clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS({
              loading: true,
            })
          );
          return this.clientsService
            .saveWithdrawalAssignment$(action.withdrawalAssignment)
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeAll();
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "WITHDRAWAL_ASSIGNMENT_SAVED"
                );
                this.store.dispatch(
                  clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS(
                    { loading: false }
                  )
                );
                return of(
                  clientsAndAccountsActions.LOAD_WITHDRAWAL_ASSIGNMENT({
                    id: action.accountId,
                  })
                );
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "WITHDRAWAL_ASSIGNMENT_SAVE_ERROR",
                  response
                );
                this.store.dispatch(
                  clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS(
                    { loading: false }
                  )
                );
                return of();
              })
            );
        }
      )
    );
  });

  updateAccountTransaction$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.UPDATE_ACCOUNT_TRANSACTION),
      withLatestFrom(this.store.select(SELECT_ACTIVE_ACCOUNT)),
      mergeMap(
        ([action, activeAccount]: [
          {
            id: string;
            description?: string;
            exchangeInformation?: boolean;
            version: string;
          },
          ExtendedGfhAccount,
        ]): Observable<Action> => {
          this.store.dispatch(
            clientsAndAccountsActions.SET_ACCOUNT_TRANSACTIONS_LOADING_STATUS({
              loading: true,
            })
          );
          return this.clientsService.updateAccountTransaction$(action).pipe(
            switchMap((): Observable<Action> => {
              if (action.description) {
                this.dialog.closeAll();
              }
              this.notificationService.show(
                ToasterSeverities.Success,
                "TRANSACTION_UPDATE_SUCCESS"
              );
              return of(
                clientsAndAccountsActions.LOAD_ACCOUNT_TRANSACTIONS({
                  id: activeAccount.id,
                })
              );
            }),
            catchError((response: HttpErrorResponse): Observable<Action> => {
              this.notificationService.show(
                ToasterSeverities.Error,
                "TRANSACTION_UPDATE_ERROR",
                response
              );
              this.store.dispatch(
                clientsAndAccountsActions.SET_ACCOUNT_TRANSACTIONS_LOADING_STATUS(
                  { loading: false }
                )
              );
              return of();
            })
          );
        }
      )
    );
  });

  loadAccountDisbursements$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_ACCOUNT_DISBURSEMENTS),
      withLatestFrom(this.store.select(SELECT_CLIENT_ACCOUNTS_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          {
            id: string;
            pagination?: Partial<TPagination>;
          },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(
            clientsAndAccountsActions.SET_ACCOUNT_DISBURSEMENTS_LOADING_STATUS({
              loading: true,
            })
          );
          return this.clientsService
            .getAccountDisbursements$(
              action.id,
              action.pagination ?? pagination
            )
            .pipe(
              switchMap(
                (
                  response:
                    | Array<DisbursementWithTax>
                    | TDisbursementsWithTaxPaginated
                ): Observable<Action> => {
                  this.store.dispatch(
                    clientsAndAccountsActions.SET_ACCOUNT_DISBURSEMENTS_LOADING_STATUS(
                      { loading: false }
                    )
                  );
                  if ("data" in response && "length" in response) {
                    const { data, ...pagination } = response;
                    return of(
                      clientsAndAccountsActions.SET_ACCOUNT_DISBURSEMENTS({
                        disbursements: data.disbursements,
                      }),
                      clientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_LIST_LENGTH(
                        {
                          length: pagination.length,
                        }
                      ),
                      clientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_PAGINATION(
                        {
                          pagination,
                        }
                      )
                    );
                  }
                  return of(
                    clientsAndAccountsActions.SET_ACCOUNT_DISBURSEMENTS({
                      disbursements: response,
                    })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  clientsAndAccountsActions.SET_ACCOUNT_DISBURSEMENTS({
                    disbursements: [],
                  }),

                  clientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_LIST_LENGTH({
                    length: 0,
                  }),
                  clientsAndAccountsActions.SET_CLIENTS_ACCOUNTS_PAGINATION({
                    pagination: undefined,
                  }),
                  clientsAndAccountsActions.SET_ACCOUNT_DISBURSEMENTS_LOADING_STATUS(
                    { loading: false }
                  )
                );
              })
            );
        }
      )
    );
  });

  closeAccount$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.CLOSE_ACCOUNT),
      mergeMap(
        (action: {
          id: string;
          accountClosingPayload: Partial<GfhAccountClosure>;
        }): Observable<Action> => {
          return this.clientsService
            .closeAccount$(action.id, action.accountClosingPayload)
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeAll();
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "ACCOUNT_TERMINATION_SAVED"
                );
                return of(
                  clientsAndAccountsActions.LOAD_ACCOUNT_DETAILS({
                    id: action.id,
                  })
                );
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "ACCOUNT_TERMINATION_ERROR",
                  response
                );
                return of();
              })
            );
        }
      )
    );
  });

  sendClosingLetter$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.SEND_CLOSING_LETTER),
      mergeMap((action: { id: string }): Observable<Action> => {
        return this.clientsService.sendClosingLetter$(action.id).pipe(
          switchMap((): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Success,
              "SEND_CLOSING_LETTER_BACKGROUND"
            );
            return of();
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "CLOSING_LETTER_SEND_ERROR",
              response
            );
            return of();
          })
        );
      })
    );
  });

  loadInternalTransferList$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_INTERNAL_TRANSFER_LIST),
      mergeMap((action: { accountId: string }): Observable<Action> => {
        this.store.dispatch(
          clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS({
            loading: true,
          })
        );
        return this.clientsService
          .getInternalTransferList$(action.accountId)
          .pipe(
            switchMap(
              (
                internalTransferList: Array<InternalTransfer>
              ): Observable<Action> => {
                this.store.dispatch(
                  clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS(
                    { loading: false }
                  )
                );
                return of(
                  clientsAndAccountsActions.SET_INTERNAL_TRANSFER_LIST({
                    internalTransferList,
                  })
                );
              }
            ),
            catchError((): Observable<Action> => {
              return of(
                clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS(
                  { loading: false }
                )
              );
            })
          );
      })
    );
  });

  saveInternalTransfer$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.SAVE_INTERNAL_TRANSFER),
      mergeMap(
        (action: {
          internalTransfer: Partial<InternalTransfer>;
          accountId: string;
        }): Observable<Action> => {
          this.store.dispatch(
            clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS({
              loading: true,
            })
          );
          return this.clientsService
            .saveInternalTransfer$(action.internalTransfer)
            .pipe(
              switchMap((): Observable<Action> => {
                this.dialog.closeAll();
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "INTERNAL_TRANSFER_SAVED"
                );
                this.store.dispatch(
                  clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS(
                    { loading: false }
                  )
                );
                return of(
                  clientsAndAccountsActions.LOAD_INTERNAL_TRANSFER_LIST({
                    accountId: action.accountId,
                  })
                );
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "INTERNAL_TRANSFER_SAVE_ERROR",
                  response
                );
                this.store.dispatch(
                  clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS(
                    { loading: false }
                  )
                );
                return of();
              })
            );
        }
      )
    );
  });

  deleteInternalTransfer$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.DELETE_INTERNAL_TRANSFER),
      concatMap(
        (action: { id: string; accountId: string }): Observable<Action> => {
          this.store.dispatch(
            clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS({
              loading: true,
            })
          );
          return this.clientsService.deleteInternalTransfer$(action.id).pipe(
            switchMap((): Observable<Action> => {
              this.store.dispatch(
                clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS(
                  { loading: false }
                )
              );
              return of(
                clientsAndAccountsActions.LOAD_INTERNAL_TRANSFER_LIST({
                  accountId: action.accountId,
                })
              );
            }),
            catchError((): Observable<Action> => {
              this.store.dispatch(
                clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS(
                  { loading: false }
                )
              );
              return of();
            })
          );
        }
      )
    );
  });

  loadPauseReasonStatus$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_PAUSE_REASON_STATUS),
      mergeMap((): Observable<Action> => {
        return this.clientsService.getPauseReasonStatus$().pipe(
          switchMap((pauseReasonStatus: boolean): Observable<Action> => {
            return of(
              clientsAndAccountsActions.SET_PAUSE_REASON_STATUS({
                pauseReasonStatus,
              })
            );
          }),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  sendFrontPageLetter$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.SEND_FRONT_PAGE_LETTER),
      mergeMap((action: { id: string }): Observable<Action> => {
        return this.clientsService.sendFrontPageLetter$(action.id).pipe(
          switchMap((): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Success,
              "LETTER_CREATED_SUCCESSFULLY"
            );
            return of();
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "LETTER_CREATION_ERROR",
              response
            );
            return of();
          })
        );
      })
    );
  });

  sendWelcomeLetter$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.SEND_WELCOME_LETTER),
      mergeMap(
        (action: {
          id: string;
          letter: { sendToClient: boolean };
        }): Observable<Action> => {
          return this.clientsService
            .sendWelcomeLetter$(action.id, action.letter)
            .pipe(
              switchMap((): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Success,
                  "MODIFICATION_LETTER_CREATED_SUCCESSFULLY"
                );
                return of();
              }),
              catchError((response: HttpErrorResponse): Observable<Action> => {
                this.notificationService.show(
                  ToasterSeverities.Error,
                  "LETTER_CREATION_ERROR",
                  response
                );
                return of();
              })
            );
        }
      )
    );
  });

  loadQuotationList$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_QUOTATIONS),
      withLatestFrom(this.store.select(SELECT_CLIENT_ACCOUNTS_PAGINATION)),
      mergeMap(
        ([action, pagination]: [
          {
            searchTerm?: string;
            pagination?: Partial<TPagination>;
          },
          Partial<TPagination> | undefined,
        ]): Observable<Action> => {
          this.store.dispatch(
            clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS({
              loading: true,
            })
          );
          return this.clientsService
            .getQuotationList$(
              action.searchTerm,
              action.pagination ?? pagination
            )
            .pipe(
              switchMap(
                (quotationList: Array<Quotation>): Observable<Action> => {
                  this.store.dispatch(
                    clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS(
                      { loading: false }
                    )
                  );
                  return of(
                    clientsAndAccountsActions.SET_QUOTATIONS({
                      quotationList: quotationList,
                    })
                  );
                }
              ),
              catchError((): Observable<Action> => {
                return of(
                  clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS(
                    { loading: false }
                  )
                );
              })
            );
        }
      )
    );
  });

  deleteQuotation$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.DELETE_QUOTATION),
      concatMap((action: { quotationId: string }): Observable<Action> => {
        this.store.dispatch(
          clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS({
            loading: true,
          })
        );
        return this.clientsService.deleteQuotation$(action.quotationId).pipe(
          switchMap((): Observable<Action> => {
            this.store.dispatch(clientsAndAccountsActions.LOAD_QUOTATIONS({}));
            return of();
          }),
          catchError((): Observable<Action> => {
            this.store.dispatch(
              clientsAndAccountsActions.SET_CLIENT_ACCOUNTS_TABLE_LOADING_STATUS(
                { loading: false }
              )
            );
            return of();
          })
        );
      })
    );
  });

  loadRiskProfiles$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_RISK_PROFILES),
      mergeMap((action: { id: string }): Observable<Action> => {
        return this.clientsService.getProductRiskProfiles$(action.id).pipe(
          concatMap(
            (
              riskProfiles: Array<CompositionRiskProfile>
            ): Observable<Action> => {
              if (riskProfiles?.length) {
                // The model versions go into the store ahead of the profiles:
                // the account form tells an Individual Composition apart by
                // its profile having no version, so profiles arriving first
                // briefly made every one of them look individual.
                const setRiskProfiles =
                  clientsAndAccountsActions.SET_RISK_PROFILES({ riskProfiles });
                return this.clientsService
                  .getCompositionProductFunds$(action.id)
                  .pipe(
                    concatMap(
                      (
                        compositionFundList: Array<CompositionVersion>
                      ): Observable<Action> =>
                        of(
                          clientsAndAccountsActions.SET_COMPOSITION_PRODUCT_FUNDS(
                            { compositionFundList }
                          ),
                          setRiskProfiles
                        )
                    ),
                    catchError((): Observable<Action> => of(setRiskProfiles))
                  );
              }
              return this.clientsService.getRiskProfiles$().pipe(
                concatMap(
                  (riskProfiles: Array<RiskProfile>): Observable<Action> => {
                    return of(
                      clientsAndAccountsActions.SET_RISK_PROFILES({
                        riskProfiles,
                      })
                    );
                  }
                ),
                catchError((): Observable<Action> => {
                  return of();
                })
              );
            }
          ),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  setImpersonationAccountRequest$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_IMPERSONATION_ACCOUNT_REQUEST),
      mergeMap((action: { id: string }): Observable<Action> => {
        return this.clientsService
          .getImpersonationAccountRequest$(action.id)
          .pipe(
            switchMap(
              (impersonationAccountRequest: string): Observable<Action> => {
                return of(
                  clientsAndAccountsActions.SET_IMPERSONATION_ACCOUNT_REQUEST({
                    impersonationAccountRequest,
                  })
                );
              }
            ),
            catchError((): Observable<Action> => {
              return of();
            })
          );
      })
    );
  });

  setAccountRiskProfiles$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_ACCOUNT_RISK_PROFILES),
      mergeMap((action: { id: string }): Observable<Action> => {
        return this.clientsService.getAccountRiskProfiles$(action.id).pipe(
          switchMap(
            (
              accountRiskProfiles: Array<AccountRiskProfile>
            ): Observable<Action> => {
              return of(
                clientsAndAccountsActions.SET_ACCOUNT_RISK_PROFILES({
                  accountRiskProfiles,
                })
              );
            }
          ),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  setFundRatioHistory$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_FUND_RATIO_HISTORY),
      mergeMap((action: { id: string }): Observable<Action> => {
        return this.clientsService.getFundRationHistory$(action.id).pipe(
          switchMap(
            (fundRatioHistory: Array<FundRatioHistory>): Observable<Action> => {
              return of(
                clientsAndAccountsActions.SET_FUND_RATIO_HISTORY({
                  fundRatioHistory,
                })
              );
            }
          ),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  reverseCancelAccount$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.REVERSE_CANCEL_ACCOUNT),
      mergeMap((action: { id: string }): Observable<Action> => {
        return this.clientsService.reverseCancelAccount$(action.id).pipe(
          switchMap((): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Success,
              "ACCOUNT_UPDATE_SUCCESS"
            );
            return of(
              clientsAndAccountsActions.LOAD_ACCOUNT_DETAILS({ id: action.id })
            );
          }),
          catchError((response: HttpErrorResponse): Observable<Action> => {
            this.notificationService.show(
              ToasterSeverities.Error,
              "ACCOUNT_UPDATE_ERROR",
              response
            );
            return of();
          })
        );
      })
    );
  });

  checkForExistingEmail$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.CHECK_FOR_EXISTING_EMAIL),
      mergeMap((action: { email: string }): Observable<Action> => {
        return this.clientsService.checkForExistingEmail$(action.email).pipe(
          switchMap((emailExists: boolean): Observable<Action> => {
            return of(
              clientsAndAccountsActions.SET_EMAIL_VALIDITY({
                emailExists: emailExists,
              })
            );
          }),
          catchError((): Observable<Action> => {
            return of();
          })
        );
      })
    );
  });

  loadAccountFunds$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.LOAD_ACCOUNT_FUNDS),
      mergeMap((action: { id: string }): Observable<Action> => {
        this.store.dispatch(
          clientsAndAccountsActions.SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS({
            loading: true,
          })
        );
        return this.clientsService.getAccountFunds$(action.id).pipe(
          switchMap(
            (accountFundList: Array<AccountFund>): Observable<Action> => {
              this.store.dispatch(
                clientsAndAccountsActions.SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS(
                  { loading: false }
                )
              );
              return of(
                clientsAndAccountsActions.SET_ACCOUNT_FUNDS({
                  accountFundList,
                })
              );
            }
          ),
          catchError((): Observable<Action> => {
            return of(
              clientsAndAccountsActions.SET_ACCOUNT_FUNDS({
                accountFundList: [],
              }),
              clientsAndAccountsActions.SET_CLIENTS_AND_ACCOUNTS_LOADING_STATUS(
                { loading: false }
              )
            );
          })
        );
      })
    );
  });

  checkForExistingLeiNumber$ = createEffect((): Observable<Action> => {
    return this.actions$.pipe(
      ofType(clientsAndAccountsActions.CHECK_FOR_EXISTING_LEI_NUMBER),
      mergeMap((action: { leiNumber: string }): Observable<Action> => {
        return this.clientsService
          .checkForExistingLeiNumber$(action.leiNumber)
          .pipe(
            switchMap((leiExists: boolean): Observable<Action> => {
              return of(
                clientsAndAccountsActions.SET_LEI_NUMBER_VALIDITY({
                  leiExists,
                })
              );
            }),
            catchError((): Observable<Action> => {
              return of();
            })
          );
      })
    );
  });

  constructor(
    private actions$: Actions,
    private clientsService: ClientsAndAccountsService,
    private transactionService: TransactionService,
    private notificationService: NotificationService,
    private dialog: DialogHelper,
    private store: Store
  ) {}
}
